/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';

// Types
import { TParamsGetApi } from '@global-types/index';

// Lib
import { apiClient } from '@lib/api-client';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import { TApiResponseGetMeasurabilities } from '@features/resources/measurabilities/types';
import { QueryConfig } from '@lib/react-query';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getProducers = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetMeasurabilities> => {
	return apiClient.get('/producers', {
		params: {
			...params,
		},
	});
};

export const getProducersHasMeasurabilitiesProjects =
	(): Promise<{ ids: string[] }> => {
		return apiClient.get(
			'/measurabilities/parent/producers'
		);
	};

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TUseGetMeasurabilitiesOptions = {
	params: Partial<TParamsGetApi>;
	queryConfig?: QueryConfig<typeof getProducers>;
};

/* -------------------------------------------------------------------------- */
/*                                    Helper                                  */
/* -------------------------------------------------------------------------- */
export const getPostsQueryOptions = (
	params: Partial<TParamsGetApi> = {}
) => {
	return queryOptions({
		queryKey: params
			? [queryKeys.app.producers.list.key, { params }]
			: [queryKeys.app.producers.list.key],
		queryFn: () => getProducers(params),
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useGetProducers = ({
	params,
	queryConfig,
}: TUseGetMeasurabilitiesOptions) => {
	const { data: producersIds } = useQuery({
		queryKey: ['producersIds'],
		queryFn: getProducersHasMeasurabilitiesProjects,
	});

	return useQuery({
		...getPostsQueryOptions(params),
		...queryConfig,

		enabled:
			!!producersIds && queryConfig?.enabled !== false,
		select: (data) => {
			if (!producersIds) return data;

			const filteredRows = data.rows.filter((producer) =>
				producersIds.ids.includes(producer.id)
			);

			return {
				...data,
				rows: filteredRows,
			};
		},
	});
};
